import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { useAuth, useHasRole, useRole } from '@/features/auth/AuthProvider';
import { useCurrentStore } from '@/hooks/useCurrentStore';
import {
  canViewCost,
  type InventoryItem,
  InventoryItemSchema,
} from '@/types/schemas';
import {
  assignSku,
  createInventoryItem,
  updateInventoryItem,
} from './inventoryQueries';
import {
  fetchCategories,
  fetchBrands,
  fetchUnits,
} from './setupQueries';
import { fetchSuppliers } from './supplierQueries';
import { SearchableSelectWithAdd } from './SearchableSelectWithAdd';
import { CategoryFormDialog } from './CategoryFormDialog';
import { BrandFormDialog } from './BrandFormDialog';
import { UnitFormDialog } from './UnitFormDialog';
import { logActivity } from '@/lib/activityLog';

export function InventoryFormDialog({
  open,
  onOpenChange,
  editing,
  defaultCategoryId,
  defaultBrandId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: InventoryItem | null;
  defaultCategoryId?: string;
  defaultBrandId?: string;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const role = useRole();
  const showCost = canViewCost(role);
  const currentStoreId = useCurrentStore((s) => s.currentStoreId);
  const canWrite = useHasRole('owner');

  const { data: categories = [] } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: fetchCategories,
    enabled: open,
  });
  const [categoryId, setCategoryId] = useState('');
  const { data: brands = [] } = useQuery({
    queryKey: ['inventory-brands', categoryId],
    queryFn: () => fetchBrands(categoryId),
    enabled: open && !!categoryId,
  });
  const { data: units = [] } = useQuery({
    queryKey: ['inventory-units'],
    queryFn: fetchUnits,
    enabled: open,
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: fetchSuppliers,
    enabled: open,
  });

  const [itemName, setItemName] = useState('');
  const [brandId, setBrandId] = useState('');
  const [brand, setBrand] = useState('');
  const [unitId, setUnitId] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [purchasePrice, setPurchasePrice] = useState(0);
  const [sellingPrice, setSellingPrice] = useState(0);
  const [lowStockThreshold, setLowStockThreshold] = useState(5);
  const [expiryDate, setExpiryDate] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [notes, setNotes] = useState('');
  const [sku, setSku] = useState('');
  const [attributeValues, setAttributeValues] = useState<Record<string, unknown>>({});
  const [categoryFormOpen, setCategoryFormOpen] = useState(false);
  const [brandFormOpen, setBrandFormOpen] = useState(false);
  const [unitFormOpen, setUnitFormOpen] = useState(false);

  const category = useMemo(
    () => categories.find((c) => c.id === categoryId),
    [categories, categoryId],
  );

  const categoryOptions = useMemo(
    () =>
      categories.map((c) => ({
        id: c.id,
        label: `${c.name} (${c.codePrefix})`,
        searchText: c.codePrefix,
      })),
    [categories],
  );

  const brandOptions = useMemo(
    () => brands.map((b) => ({ id: b.id, label: b.name })),
    [brands],
  );

  const unitOptions = useMemo(
    () => units.map((u) => ({ id: u.id, label: u.name })),
    [units],
  );

  const selectedUnitName = useMemo(
    () => units.find((u) => u.id === unitId)?.name ?? 'piece',
    [units, unitId],
  );

  const categoryBrands = brands;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setCategoryId(editing.categoryId ?? categories.find((c) => c.name === editing.category)?.id ?? '');
      setItemName(
        editing.itemName ||
          [editing.brand, editing.subBrand, editing.variant].filter(Boolean).join(' ').trim(),
      );
      setBrandId(editing.brandId ?? '');
      setBrand(editing.brand);
      setUnitId(units.find((u) => u.name === (editing.unit ?? 'piece'))?.id ?? '');
      setQuantity(editing.quantity ?? 0);
      setPurchasePrice(editing.purchasePrice);
      setSellingPrice(editing.sellingPrice);
      setLowStockThreshold(editing.lowStockThreshold ?? 5);
      setExpiryDate(
        editing.expiryDate
          ? new Date(editing.expiryDate as string).toISOString().split('T')[0]
          : '',
      );
      setSupplierId(editing.supplierId ?? '');
      setNotes(editing.notes ?? '');
      setSku(editing.sku ?? '');
      setAttributeValues((editing.attributeValues as Record<string, unknown>) ?? {});
    } else {
      setCategoryId(defaultCategoryId ?? categories[0]?.id ?? '');
      setItemName('');
      setBrandId(defaultBrandId ?? '');
      setBrand('');
      setUnitId(units.find((u) => u.name === 'piece')?.id ?? units[0]?.id ?? '');
      setQuantity(0);
      setPurchasePrice(0);
      setSellingPrice(0);
      setLowStockThreshold(5);
      setExpiryDate('');
      setSupplierId('');
      setNotes('');
      setSku('');
      setAttributeValues({});
    }
  }, [open, editing, defaultCategoryId, defaultBrandId, categories, units]);

  useEffect(() => {
    if (!open || editing) return;
    if (defaultBrandId && categoryBrands.some((b) => b.id === defaultBrandId)) {
      const matched = categoryBrands.find((b) => b.id === defaultBrandId);
      if (matched) {
        setBrandId(matched.id);
        setBrand(matched.name);
      }
    }
  }, [open, editing, defaultBrandId, categoryBrands]);

  useEffect(() => {
    if (category && !editing) {
      setLowStockThreshold(category.lowStockThresholdDefault);
    }
  }, [category, editing]);

  useEffect(() => {
    if (!editing && brandId) {
      const matched = categoryBrands.find((b) => b.id === brandId);
      if (matched) setBrand(matched.name);
    }
  }, [brandId, categoryBrands, editing]);

  function handleCategoryChange(nextCategoryId: string) {
    setCategoryId(nextCategoryId);
    setBrandId('');
    setBrand('');
  }

  useEffect(() => {
    if (!open || !categoryId || !brandId) return;
    if (!categoryBrands.some((b) => b.id === brandId)) {
      setBrandId('');
      setBrand('');
    }
  }, [open, categoryId, brandId, categoryBrands]);

  function handleBrandChange(nextBrandId: string) {
    setBrandId(nextBrandId);
    const matched = categoryBrands.find((b) => b.id === nextBrandId);
    setBrand(matched?.name ?? '');
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!editing && !currentStoreId) throw new Error('Choose a store before creating a product');
      if (!category) throw new Error('Select a category');
      if (!brandId && !brand.trim()) throw new Error('Brand is required');
      if (!itemName.trim()) throw new Error('Product name is required');
      if (sellingPrice <= 0) throw new Error('Selling price must be positive');
      if (showCost && purchasePrice > sellingPrice) {
        throw new Error('Cost price cannot exceed selling price');
      }
      for (const attr of category.attributeSchema) {
        if (
          attr.required &&
          (attributeValues[attr.key] === undefined || attributeValues[attr.key] === '')
        ) {
          throw new Error(`${attr.label} is required`);
        }
      }
      if (category.hasExpiry && !editing && !expiryDate) {
        throw new Error('Expiry date is required for this category');
      }

      const resolvedBrand = brand.trim() || categoryBrands.find((b) => b.id === brandId)?.name || '';
      const resolvedBrandId = brandId || categoryBrands.find((b) => b.name === resolvedBrand)?.id || '';

      const payload = InventoryItemSchema.parse({
        itemName: itemName.trim(),
        categoryId: category.id,
        category: category.name,
        brandId: resolvedBrandId,
        brand: resolvedBrand,
        subBrand: editing?.subBrand ?? '',
        variant: editing?.variant ?? '',
        sku: sku || editing?.sku || '',
        unit: selectedUnitName,
        length: editing?.length ?? '',
        width: editing?.width ?? '',
        thickness: editing?.thickness ?? '',
        attributeValues,
        purchasePrice: showCost ? purchasePrice : editing?.purchasePrice ?? 0,
        sellingPrice,
        quantity: 0,
        lowStockThreshold,
        expiryDate: category.hasExpiry && expiryDate ? expiryDate : '',
        supplierId,
        notes,
        source: editing?.source ?? 'manual',
        isActive: true,
      });

      if (editing) {
        await updateInventoryItem(editing.id, {
          ...payload,
          expiryDate: category.hasExpiry && expiryDate ? expiryDate : null,
        } as Partial<InventoryItem>);
      } else {
        let newSku = sku;
        if (!newSku) {
          newSku = await assignSku(category.codePrefix);
          setSku(newSku);
        }
        await createInventoryItem(
          { ...payload, sku: newSku },
          user?.uid ?? null,
          {
            storeId: currentStoreId ?? undefined,
            initialStock: quantity > 0 ? quantity : 0,
          },
        );
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['store-inventory-map'] });
      qc.invalidateQueries({ queryKey: ['stock-movements'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast({ title: editing ? 'Product updated' : 'Product created', variant: 'success' });
      logActivity({
        action: editing ? 'product_edited' : 'product_created',
        title: editing ? 'Product Edited' : 'Product Created',
        description: `${editing ? 'Updated' : 'Created'} "${itemName.trim()}" in ${category?.name ?? 'unknown category'}`,
        actorId: user?.uid ?? null,
        actorName: user?.displayName ?? user?.email ?? null,
        entityType: 'product',
        entityId: editing?.id ?? 'new',
        metadata: { itemName: itemName.trim(), category: category?.name, brand },
      });
      onOpenChange(false);
    },
    onError: (e) =>
      toast({ title: 'Save failed', description: String(e), variant: 'destructive' }),
  });

  if (!canWrite) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit product' : 'Create product'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Update the product details. Stock changes use the inventory ledger.'
              : 'The selected store receives the opening stock. Then choose category, brand, and product details.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <SearchableSelectWithAdd
              label="Category *"
              value={categoryId}
              onValueChange={handleCategoryChange}
              options={categoryOptions}
              placeholder="Select category"
              searchPlaceholder="Search categories…"
              emptyMessage="No categories found"
              onAdd={() => setCategoryFormOpen(true)}
              addTitle="Add category"
            />
          </div>

          <div className="sm:col-span-2">
            <SearchableSelectWithAdd
              label="Brand *"
              value={brandId}
              onValueChange={handleBrandChange}
              options={brandOptions}
              placeholder={categoryId ? 'Select brand' : 'Select a category first'}
              searchPlaceholder="Search brands…"
              emptyMessage={categoryId ? 'No brands in this category' : 'Select a category first'}
              disabled={!categoryId}
              addDisabled={!categoryId}
              onAdd={() => setBrandFormOpen(true)}
              addTitle="Add brand"
            />
          </div>

          <div className="sm:col-span-2">
            <Label>Product name *</Label>
            <Input
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              placeholder="e.g. 18mm BWP Plywood Sheet"
            />
          </div>

          {editing?.sku && (
            <div className="sm:col-span-2">
              <Label>SKU</Label>
              <Input value={editing.sku} disabled className="font-mono text-sm" />
            </div>
          )}

          <div>
            <SearchableSelectWithAdd
              label="Unit"
              value={unitId}
              onValueChange={setUnitId}
              options={unitOptions}
              placeholder="Select unit"
              searchPlaceholder="Search units…"
              emptyMessage="No units found"
              onAdd={() => setUnitFormOpen(true)}
              addTitle="Add unit"
            />
          </div>
          <div>
            <Label>Quantity</Label>
            <Input
              type="number"
              min={0}
              value={quantity || ''}
              placeholder="0"
              onChange={(e) => setQuantity(Number(e.target.value))}
              disabled={!!editing}
            />
          </div>
          {showCost && (
            <div>
              <Label>Cost price</Label>
              <Input type="number" min={0} value={purchasePrice || ''} placeholder="0.00" onChange={(e) => setPurchasePrice(Number(e.target.value))} />
            </div>
          )}
          <div>
            <Label>Sell price *</Label>
            <Input type="number" min={0} value={sellingPrice || ''} placeholder="0.00" onChange={(e) => setSellingPrice(Number(e.target.value))} />
          </div>
          <div>
            <Label>Low stock threshold</Label>
            <Input type="number" min={0} value={lowStockThreshold || ''} placeholder="0" onChange={(e) => setLowStockThreshold(Number(e.target.value))} />
          </div>
          {category?.hasExpiry && (
            <div>
              <Label>Expiry date</Label>
              <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
            </div>
          )}
          <div className="sm:col-span-2">
            <Label>Supplier</Label>
            <Select value={supplierId || '__none__'} onValueChange={(v) => setSupplierId(v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {category?.attributeSchema.map((attr) => (
            <div key={attr.key} className="sm:col-span-2">
              <Label>{attr.label}{attr.required ? ' *' : ''}</Label>
              {attr.type === 'select' && attr.options ? (
                <Select
                  value={String(attributeValues[attr.key] ?? '')}
                  onValueChange={(v) => setAttributeValues((p) => ({ ...p, [attr.key]: v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {attr.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={attr.type === 'number' ? 'number' : 'text'}
                  value={String(attributeValues[attr.key] ?? '')}
                  onChange={(e) =>
                    setAttributeValues((p) => ({
                      ...p,
                      [attr.key]: attr.type === 'number' ? Number(e.target.value) : e.target.value,
                    }))
                  }
                />
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="gold"
            disabled={save.isPending || (!editing && !currentStoreId) || !categoryId || (!brandId && !brand.trim())}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>

      <CategoryFormDialog
        open={categoryFormOpen}
        onOpenChange={setCategoryFormOpen}
        editing={null}
        onCreated={(id) => {
          setCategoryId(id);
          setBrandId('');
          setBrand('');
        }}
      />
      <BrandFormDialog
        open={brandFormOpen}
        onOpenChange={setBrandFormOpen}
        defaultCategoryId={categoryId}
        onCreated={({ id, name: brandName }) => {
          setBrandId(id);
          setBrand(brandName);
          qc.invalidateQueries({ queryKey: ['inventory-brands', categoryId] });
        }}
      />
      <UnitFormDialog
        open={unitFormOpen}
        onOpenChange={setUnitFormOpen}
        onCreated={({ id }) => setUnitId(id)}
      />
    </Dialog>
  );
}
